import React, { useEffect } from 'react';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { store } from './store/store';
import AppRouter from './routes/AppRouter';
import SessionProvider from './components/Auth/SessionProvider';
import ThemeProvider from './components/Theme/ThemeProvider';
import AlertProvider from './components/UI/AlertProvider';
import { authAPI } from './services/api';
import { logWarning } from './utils/errorLogger';
import { cleanupOldTokens, cleanupFullUserData, markMigrationComplete, isMigrationComplete } from './utils/tokenCleanup';

// Initialize API health check
const initializeApp = async () => {
  try {
    await authAPI.healthCheck();
  } catch (error) {
    logWarning('Backend connection failed - running in offline mode', error);
  }
};

// Perform security cleanup on every app startup
// SECURITY: Tokens should NEVER be in localStorage/sessionStorage - they're in httpOnly cookies
const performStartupCleanup = () => {
  try {
    // ALWAYS clean up tokens from storage (they should never be there)
    // Tokens are now stored in httpOnly cookies, not accessible to JavaScript
    const tokenCleanupResult = cleanupOldTokens();
    if (tokenCleanupResult.cleaned.length > 0) {
      logWarning('[Security] Removed tokens from browser storage', { cleaned: tokenCleanupResult.cleaned });
    }
    
    // Always clean up full user data (PII) from storage for security
    const userDataCleanupResult = cleanupFullUserData();
    if (userDataCleanupResult.cleaned.length > 0) {
      logWarning('[Security] Migrated user data to minimal format', { cleaned: userDataCleanupResult.cleaned });
    }
    
    // Mark migration as complete if not already marked
    if (!isMigrationComplete()) {
      markMigrationComplete();
    }
  } catch (error) {
    // Don't interrupt app startup if cleanup fails
    logWarning('Startup cleanup failed', error);
  }
};

const App: React.FC = () => {
  useEffect(() => {
    // Perform security cleanup on every app startup
    // This ensures tokens and PII are never stored in browser storage
    performStartupCleanup();
    
    // Initialize API health check
    initializeApp();
  }, []);

  return (
    <Provider store={store}>
      <BrowserRouter>
        <SessionProvider>
          <ThemeProvider>
            <AlertProvider>
              <div className="App">
                <AppRouter />
              </div>
            </AlertProvider>
          </ThemeProvider>
        </SessionProvider>
      </BrowserRouter>
    </Provider>
  );
};

export default App;